import React, { useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Form, Input, Modal, Radio, Select, Space, Tag, Upload, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import {
  createContentItem,
  getContentItem,
  listContentChannels,
  uploadContentAttachment,
  uploadContentImage,
  updateContentItem,
} from '../../../services/content';
import { ContentAttachment, ContentChannel, ContentItemPayload } from '../../../types/content';
import RichTextEditor from '../../../components/RichTextEditor';
import { sanitizeAndNormalizeRichText } from '../../../utils/richText';
import SafeVideoEmbed from '../../../components/SafeVideoEmbed';
import { getYoutubeThumbnailUrl } from '../../../utils/videoLinks';

const RESOURCE_CHANNEL_PREFIX = 'resource_';
const CONTENT_TYPE_OPTIONS = [
  { label: '文章', value: 'article' },
  { label: '图集', value: 'photo_set' },
  { label: '视频', value: 'video' },
];

const formatFileSize = (bytes?: number | null): string => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const { TextArea } = Input;

const extractErrorText = (error: any): string => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.detail) return String(data.detail);
  if (typeof data === 'object' && data) {
    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (firstValue) return String(firstValue);
  }
  return '保存失败';
};

const ContentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = id === 'new';
  const parsedId = Number(id);
  const isValidEditId = Number.isInteger(parsedId) && parsedId > 0;

  const [form] = Form.useForm<ContentItemPayload & { pointsText?: string; slugText?: string }>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  // 附件是"一条内容对应多个文件"的列表，用普通 state 管理增删，不走 antd Form 的字段绑定
  // （数组类字段用 Form.List 会更绕，这里增删逻辑简单，直接 state 更直观也更不容易出 bug）。
  const [attachments, setAttachments] = useState<ContentAttachment[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [channels, setChannels] = useState<ContentChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<number | undefined>(undefined);
  // antd Form 里 useWatch 拿不到还没渲染出来的字段初值很方便，但这里表单是动态 setFieldsValue 回填的，
  // 用一个额外的 state 跟随 channel 选择即可，逻辑更直接。
  const selectedChannelKey = useMemo(
    () => channels.find((c) => c.id === selectedChannelId)?.key || '',
    [channels, selectedChannelId]
  );
  const isResourceChannel = selectedChannelKey.startsWith(RESOURCE_CHANNEL_PREFIX);
  const isResourceSharingChannel = selectedChannelKey === 'resource_sharing';

  const channelOptions = useMemo(
    () => channels.map((c) => ({ label: `${c.name} (${c.key})`, value: c.id })),
    [channels]
  );

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        const channelData = await listContentChannels();
        setChannels(channelData);

        if (!isCreate && id) {
          if (!isValidEditId) {
            message.error('内容ID无效');
            return;
          }
          const detail = await getContentItem(parsedId);
          const pointsText = Array.isArray(detail.extra_json?.points)
            ? detail.extra_json.points.join('\n')
            : '';
          const slugText = String(detail.extra_json?.slug || '');
          form.setFieldsValue({
            channel: detail.channel,
            title: detail.title,
            summary: detail.summary,
            body: detail.body,
            image_url: detail.image_url,
            link_url: detail.link_url,
            pointsText,
            slugText,
            slug: detail.slug || undefined,
            content_type: detail.content_type || 'article',
            video_url_youtube: detail.video_url_youtube || '',
            video_url_bilibili: detail.video_url_bilibili || '',
            is_original: detail.is_original !== false,
            source_name: detail.source_name || '',
            source_url: detail.source_url || '',
            seo_title: detail.seo_title || '',
            seo_description: detail.seo_description || '',
          });
          setAttachments(Array.isArray(detail.attachments) ? detail.attachments : []);
          setSelectedChannelId(detail.channel);
        }
      } catch (error) {
        message.error('加载内容详情失败');
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [id, isCreate, form, isValidEditId, parsedId]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const points = (values.pointsText || '')
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean);
      const slug = String(values.slugText || '').trim().toLowerCase();
      const extra_json: Record<string, any> = {};
      if (points.length) extra_json.points = points;
      if (slug) extra_json.slug = slug;

      const payload: ContentItemPayload = {
        channel: values.channel,
        title: values.title,
        summary: values.summary || '',
        body: sanitizeAndNormalizeRichText(values.body || ''),
        image_url: values.image_url || '',
        link_url: values.link_url || '',
        is_active: true,
        extra_json,
        slug: values.slug ? String(values.slug).trim().toLowerCase() : undefined,
        content_type: values.content_type || 'article',
        video_url_youtube: values.video_url_youtube || '',
        video_url_bilibili: values.video_url_bilibili || '',
        is_original: values.is_original !== false,
        source_name: values.is_original === false ? values.source_name || '' : '',
        source_url: values.is_original === false ? values.source_url || '' : '',
        seo_title: values.seo_title || '',
        seo_description: values.seo_description || '',
        attachments: attachments.filter((a) => a.url),
      };

      const persist = async () => {
        setSubmitting(true);
        try {
          if (isCreate) {
            await createContentItem(payload);
            message.success('创建成功');
          } else if (id) {
            if (!isValidEditId) {
              message.error('内容ID无效');
              return;
            }
            await updateContentItem(parsedId, payload);
            message.success('更新成功');
          }
          navigate('/dashboard/admin/content/list');
        } catch (error: any) {
          message.error(extractErrorText(error));
        } finally {
          setSubmitting(false);
        }
      };

      // 资源中心的内容在列表页是图文卡片，没有封面图、也没有能自动取到缩略图的 YouTube
      // 视频链接时，最终会显示固定的品牌占位图。这里只提醒，不强制拦截。
      const willHaveNoThumbnail =
        isResourceChannel && !payload.image_url && !getYoutubeThumbnailUrl(payload.video_url_youtube);
      if (willHaveNoThumbnail) {
        Modal.confirm({
          title: '未上传封面图',
          content: '当前内容没有封面图，视频类内容也没有可自动取图的 YouTube 链接，前台列表页将显示默认的品牌占位图。是否仍要保存？',
          okText: '仍然保存',
          cancelText: '返回补充封面图',
          onOk: () => persist(),
        });
        return;
      }

      await persist();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(extractErrorText(error));
    }
  };

  const handleUploadImage = async (file: File) => {
    try {
      setImageUploading(true);
      const url = await uploadContentImage(file);
      if (!url) {
        message.error('图片上传失败');
        return false;
      }
      form.setFieldValue('image_url', url);
      message.success('图片上传成功');
    } catch (error: any) {
      message.error(extractErrorText(error));
    } finally {
      setImageUploading(false);
    }
    return false;
  };

  const handleUploadAttachment = async (file: File) => {
    try {
      setAttachmentUploading(true);
      const result = await uploadContentAttachment(file);
      if (!result.url) {
        message.error('附件上传失败');
        return false;
      }
      setAttachments((prev) => [...prev, { url: result.url, file_name: result.file_name, file_size: result.file_size }]);
      message.success('附件上传成功');
    } catch (error: any) {
      message.error(extractErrorText(error));
    } finally {
      setAttachmentUploading(false);
    }
    return false;
  };

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800">{isCreate ? '新建内容' : '编辑内容'}</h1>
          <Button type="link" icon={<InfoCircleOutlined />} onClick={() => setGuideOpen(true)}>
            图片/跳转链接说明
          </Button>
        </div>
        <p className="mt-1 text-gray-600">可用于首页轮播、公告、FAQ、系统使用指南</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        disabled={loading}
        initialValues={{ content_type: 'article', is_original: true }}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Form.Item name="channel" label="所属频道" rules={[{ required: true, message: '请选择频道' }]}>
            <Select
              placeholder="请选择频道"
              options={channelOptions}
              onChange={(v) => setSelectedChannelId(v)}
            />
          </Form.Item>
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input placeholder="请输入标题" />
          </Form.Item>
          <Form.Item
            label="图片链接"
            extra={
              isResourceChannel
                ? '建议上传封面图：资源中心列表页按图文卡片展示，不上传的话会显示默认的品牌占位图，不如真实图片吸引点击'
                : '建议上传一张配图，部分页面会用它作为展示图'
            }
          >
            {/* 图片链接的实际输入框必须直接是这个 name Form.Item 的唯一子元素，antd 才能把
                value/onChange 接到它身上；之前中间隔了一层 <Space>，导致这个框既不显示已有
                值（编辑已有内容、或点"本地上传图片"之后都看不出来），手动在框里输入/粘贴
                也存不进表单。上传按钮、预览图、说明文字都放在外层这个不带 name 的 Form.Item
                里做纯布局，不受影响。 */}
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Form.Item name="image_url" noStyle>
                <Input placeholder="可粘贴图片 URL，或使用下方本地上传" allowClear />
              </Form.Item>
              <Space>
                <Upload
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  showUploadList={false}
                  beforeUpload={handleUploadImage}
                >
                  <Button icon={<UploadOutlined />} loading={imageUploading}>
                    本地上传图片
                  </Button>
                </Upload>
                <span className="text-xs text-gray-500">支持 png/jpeg/webp/gif/svg，大小不超过 5MB</span>
              </Space>
              <Form.Item shouldUpdate noStyle>
                {() =>
                  form.getFieldValue('image_url') ? (
                    <img
                      src={form.getFieldValue('image_url')}
                      alt="uploaded-preview"
                      className="max-h-[180px] w-full rounded border border-gray-200 object-contain"
                    />
                  ) : null
                }
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="link_url" label="跳转链接">
            <Input placeholder="可选，例如 https://..." />
          </Form.Item>
          <div />
          <Form.Item name="summary" label="摘要" className="md:col-span-2">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="body" label="正文（富文本）" className="md:col-span-2">
            <RichTextEditor uploadImage={uploadContentImage} />
          </Form.Item>
          <Form.Item
            name="pointsText"
            label="轮播要点（每行一条）"
            className="md:col-span-2"
            extra="仅轮播频道需要，系统将保存到 extra_json.points"
          >
            <TextArea rows={6} placeholder="第一条\n第二条\n第三条" />
          </Form.Item>
          <Form.Item
            name="slugText"
            label="通用内容路径 Slug"
            className="md:col-span-2"
            extra="仅通用内容频道建议填写，例如 promo-2026-summer；前台详情地址为 /content/{slug}"
          >
            <Input placeholder="例如：promo-2026-summer（仅小写字母/数字/中划线）" />
          </Form.Item>

          {isResourceChannel ? (
            <>
              <div className="md:col-span-2">
                <Tag color="blue">资源中心板块专用字段</Tag>
              </div>
              <Form.Item
                name="slug"
                label="URL Slug"
                rules={[{ required: true, message: '请填写 slug，用于生成详情页地址' }]}
                extra="仅小写字母/数字/中划线，如 how-to-pick-a-warehouse；详情页地址为 /resources/板块/该slug"
              >
                <Input placeholder="例如：how-to-pick-a-warehouse" />
              </Form.Item>
              <Form.Item name="content_type" label="内容形式">
                <Select options={CONTENT_TYPE_OPTIONS} />
              </Form.Item>
              <div className="md:col-span-2 text-xs text-gray-500">
                YouTube 和 B站可以同时填写：同一条内容挂两个平台的版本，前台会按访客情况展示/切换，
                国内客户看不了 YouTube、部分海外客户上不了 B站，两个都填能兼顾两边。留空表示该平台没有对应版本。
              </div>
              <div className="md:col-span-2">
                <Form.Item
                  name="video_url_bilibili"
                  label="B站视频链接"
                  extra="仅支持 bilibili.com / b23.tv 链接"
                >
                  <Input placeholder="https://www.bilibili.com/video/BV..." />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {() =>
                    form.getFieldValue('video_url_bilibili') ? (
                      <div style={{ maxWidth: 400, marginBottom: 16, marginTop: -8 }}>
                        <SafeVideoEmbed videoUrl={form.getFieldValue('video_url_bilibili')} title="B站视频预览" />
                      </div>
                    ) : null
                  }
                </Form.Item>
              </div>
              <div className="md:col-span-2">
                <Form.Item
                  name="video_url_youtube"
                  label="YouTube视频链接"
                  extra="仅支持 youtube.com / youtu.be 链接"
                >
                  <Input placeholder="https://www.youtube.com/watch?v=..." />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {() =>
                    form.getFieldValue('video_url_youtube') ? (
                      <div style={{ maxWidth: 400, marginBottom: 16, marginTop: -8 }}>
                        <SafeVideoEmbed videoUrl={form.getFieldValue('video_url_youtube')} title="YouTube视频预览" />
                      </div>
                    ) : null
                  }
                </Form.Item>
              </div>

              <Form.Item name="is_original" label="原创 / 转载">
                <Radio.Group>
                  <Radio value={true}>原创</Radio>
                  <Radio value={false}>转载</Radio>
                </Radio.Group>
              </Form.Item>
              <div />
              <Form.Item shouldUpdate noStyle>
                {() =>
                  form.getFieldValue('is_original') === false ? (
                    <>
                      <Form.Item
                        name="source_name"
                        label="转载来源名称"
                        rules={[{ required: true, message: '转载内容必须填写来源名称' }]}
                      >
                        <Input placeholder="例如：XX行业媒体" />
                      </Form.Item>
                      <Form.Item name="source_url" label="转载来源链接">
                        <Input placeholder="原文链接，用于标注出处" />
                      </Form.Item>
                    </>
                  ) : null
                }
              </Form.Item>

              {isResourceSharingChannel ? (
                <Form.Item label="可下载附件（可上传多个）" className="md:col-span-2">
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <Upload
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.gif,.mp4,.mov"
                      showUploadList={false}
                      beforeUpload={handleUploadAttachment}
                    >
                      <Button icon={<UploadOutlined />} loading={attachmentUploading}>
                        上传附件
                      </Button>
                    </Upload>
                    <span className="text-xs text-gray-500">
                      支持 pdf/word/excel（≤20MB）、图片（≤5MB）、视频 mp4/mov（≤200MB，大文件建议改用外部视频链接）；可重复点击上传多个文件
                    </span>
                    {attachments.length ? (
                      <div className="space-y-1">
                        {attachments.map((a, idx) => (
                          <div key={`${a.url}-${idx}`} className="flex items-center gap-2 text-sm text-gray-700">
                            <span>
                              {idx + 1}. {a.file_name || '文件'}
                              {formatFileSize(a.file_size) ? (
                                <span className="text-gray-400"> · {formatFileSize(a.file_size)}</span>
                              ) : null}
                            </span>
                            <Button
                              size="small"
                              danger
                              onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                            >
                              移除
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </Space>
                </Form.Item>
              ) : null}
            </>
          ) : null}

          <Collapse
            className="md:col-span-2"
            items={[
              {
                key: 'seo',
                label: 'SEO 设置（可选，留空则使用标题/摘要）',
                children: (
                  <>
                    <div className="mb-3 text-xs leading-5 text-gray-500">
                      写成一句完整的话，把 1-2 个核心关键词自然带进去即可，不要写成用空格/顿号分隔的一串短语——
                      Google 早就不看关键词堆砌，百度权重也很低，堆砌反而在搜索结果里显得像垃圾信息、影响点击率。
                      标题建议 15-30 个汉字以内，摘要建议 50-80 个汉字左右，太长会被搜索结果截断。
                    </div>
                    <Form.Item name="seo_title" label="SEO 标题">
                      <Input placeholder="留空则使用上面的标题" />
                    </Form.Item>
                    <Form.Item name="seo_description" label="SEO 摘要">
                      <TextArea rows={2} placeholder="留空则使用上面的摘要" maxLength={300} showCount />
                    </Form.Item>
                  </>
                ),
              },
            ]}
          />
        </div>
      </Form>

      <Space>
        <Button onClick={() => navigate('/dashboard/admin/content/list')}>返回</Button>
        {!isCreate && id ? (
          <Button onClick={() => navigate(`/dashboard/admin/content/preview/${id}`)}>打开预览页</Button>
        ) : null}
        <Button onClick={() => setPreviewOpen(true)}>预览当前内容</Button>
        <Button type="primary" loading={submitting} onClick={handleSubmit}>
          保存
        </Button>
      </Space>

      <Modal
        title="内容预览（当前编辑态）"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={980}
      >
        <div className="space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">{form.getFieldValue('title') || '未填写标题'}</h2>
          {form.getFieldValue('summary') ? (
            <p className="text-gray-600">{form.getFieldValue('summary')}</p>
          ) : null}
          {form.getFieldValue('image_url') ? (
            <img
              src={form.getFieldValue('image_url')}
              alt={form.getFieldValue('title') || 'preview'}
              className="max-h-[280px] w-full rounded object-cover"
            />
          ) : null}
          <div
            className="prose content-rich-text max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeAndNormalizeRichText(form.getFieldValue('body') || '') }}
          />
        </div>
      </Modal>

      <Modal title="字段使用说明" open={guideOpen} onCancel={() => setGuideOpen(false)} footer={null} width={760}>
        <div className="space-y-4 text-sm leading-7 text-gray-700">
          <div>
            <p className="font-semibold text-gray-900">图片链接（image_url）</p>
            <p>
              可直接填写 URL，也可使用“本地上传图片”按钮自动回填。当前前台已在首页轮播（home_carousel）消费该字段，展示为轮播右侧图片。
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">跳转链接（link_url）</p>
            <p>
              支持外部链接（http/https）和站内路径（如 /service/warehouse-3pl）。当前前台已在首页轮播消费该字段，点击轮播图片时触发跳转。
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">适用范围说明</p>
            <p>
              以上字段在所有频道都可提交，但是否生效取决于对应频道页面是否显式消费该字段。当前 FAQ/系统使用指南默认按正文内容渲染，未默认使用 image_url/link_url。
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">单通用频道发布说明</p>
            <p>
              若使用通用内容频道（general_content），建议填写“通用内容路径 Slug”，发布后可通过 /content/{'{slug}'} 访问详情页。
              示例：填写 promo-2026-summer，则访问地址为 /content/promo-2026-summer。
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContentEditPage;

